import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { glass } from "./portal";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { askAssistant } from "@/lib/portal/ai.server";
import { motion } from "framer-motion";

export const Route = createFileRoute("/_authenticated/portal/assistant")({
  component: AssistantPage,
});

const SUGGESTIONS = [
  "Come posso migliorare la SEO?",
  "Aggiungi una pagina contatti",
  "Quante revisioni ho ancora?",
  "Come cambio il dominio?",
];

type Msg = { role: "user" | "assistant"; content: string };

function AssistantPage() {
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", content: "Ciao 👋 Sono l'AI Assistant del portale FlashWeb. Come posso aiutarti?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content) return;
    setInput("");
    const next = [...messages, { role: "user" as const, content }];
    setMessages(next);
    setLoading(true);
    try {
      const { content: reply } = await askAssistant({ data: { messages: next } });
      setMessages([...next, { role: "assistant", content: reply || "…" }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: `Errore: ${e instanceof Error ? e.message : "unknown"}` }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center"><Sparkles className="h-5 w-5 text-black" /></div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI Assistant</h1>
          <p className="text-white/60 text-sm">Risposte immediate su SEO, revisioni, ticket, upgrade.</p>
        </div>
      </div>

      <div className={`${glass} p-6 space-y-4 min-h-[500px] flex flex-col`}>
        <div className="flex-1 space-y-3 overflow-y-auto">
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-black" : "bg-white/5 border border-white/10"}`}>
                {m.content}
              </div>
            </motion.div>
          ))}
          {loading && <div className="text-white/40 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Sto pensando...</div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white/70">{s}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Fai una domanda..." className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
          <button disabled={loading} className="p-3 rounded-xl bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-black"><Send className="h-4 w-4" /></button>
        </form>
      </div>
    </div>
  );
}
